import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import {
  Link,
  Stack,
  Alert,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useUser from 'hooks/useUser';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { errorMonitor } from 'events';
import { useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { GetUserDocument, useSignInMutation } from 'generated/graphql';
import { client } from 'index';

// ----------------------------------------------------------------------
type InitialValues = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};
export default function LoginForm() {
  const [loginUser, { data, error: loginError }] = useSignInMutation({
    // refetchQueries: [GetUserDocument],
    // awaitRefetchQueries: true,
    onCompleted(data) {
      localStorage.setItem('token', data.userLogin.token);
    }
  });
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik<any>({
    initialValues: {
      email: '',
      password: '',
      remember: true
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        const user = await loginUser({
          variables: {
            email: values.email,
            password: values.password
          }
        }).then((data) => {
          return data;
        });
        if (user.data?.userLogin.token) {
          // await client.clearStore();
          localStorage.setItem('token', user.data.userLogin.token);
          await client.refetchQueries({ include: [GetUserDocument] });
        }
        if (user) {
          navigate(PATH_DASHBOARD.root);
        }
        // if (reduxPromiseResult.success) {
        //   enqueueSnackbar('Login success', {
        //     variant: 'success',
        //     action: (key) => (
        //       <IconButton size="small" onClick={() => closeSnackbar(key)}>
        //         <Icon icon={closeFill} />
        //       </IconButton>
        //     )
        //   });
        // } else {
        // }

        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        let message: string;
        if (error.status === 403 || error.message?.search('403')) {
          message = error.message;
        } else {
          message = error.message;
        }
        enqueueSnackbar(message, {
          variant: 'error',
          action: (key) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </IconButton>
          )
        });
        resetForm();
        if (isMountedRef.current) {
          setSubmitting(false);
          setErrors({ afterSubmit: error.message });
        }
      }
    }
  });

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {loginError?.message && <Alert severity="error">{loginError.message}</Alert>}

          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Email address"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          {/* <FormControlLabel
            control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
            label="Remember me"
          /> */}

          <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
            Forgot password?
          </Link>
        </Stack>

        <LoadingButton
          sx={{ marginTop: 2 }}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
